import AddIcon from "@mui/icons-material/Add";
import { Button } from "@mui/material";
import { useState } from "react";
import { toast } from "sonner";

import type { CountryCodes, LanguageCodes } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useCreateProjectMutation } from "Projects/Api/Mutations/useCreateProjectMutation";
import type { CreateProjectFormData } from "Projects/Forms/createProjectFormSchema";
import { dateToApiString } from "Shared/Utils/Format/date";
import { CreateProjectModal } from "./CreateProjectModal";

export const NewProjectButton = (): React.ReactNode => {
  const { t } = useResource();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const createProjectMutation = useCreateProjectMutation();

  const handleCreateProject = async (projectData: CreateProjectFormData) => {
    await createProjectMutation.mutateAsync({
      name: projectData.name,
      clientID: projectData.clientID,
      moduleID: projectData.moduleID,
      industry: projectData.industry,
      language: projectData.language as LanguageCodes,
      licensedCountries: projectData.licensedCountries as CountryCodes[],
      jobsLimit: projectData.jobsLimit,
      startDate: dateToApiString(projectData.startDate),
      endDate: dateToApiString(projectData.endDate),
      exclusiveContracts: projectData.hasExclusiveContracts
        ? projectData.exclusiveContracts.map((ec) => ({
            actorID: ec.actorID,
            price: ec.price,
            jobLimit: ec.jobLimit,
          }))
        : [],
    });

    toast.success(t(Resources.Projects.CreateProjectModal.SuccessMessage));
    setIsModalOpen(false);
  };

  return (
    <>
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={() => setIsModalOpen(true)}
      >
        {t(Resources.Common.NewProject)}
      </Button>
      <CreateProjectModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleCreateProject}
        serverError={createProjectMutation.error}
      />
    </>
  );
};
